import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Typography,
    Box,
    CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { getFileForReview } from "../utils/api";
import Loader from "./Loader";

const FileViewerPopup = ({ open, handleClose, version, publishDate, componentName, flag }) => {
    const [content, setContent] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const fetchContent = async () => {
        if (!version || !componentName) return;
        try {
            setError("");
            setLoading(true);

            const data = await getFileForReview(componentName, version, flag);
            setContent(data);
        } catch (err) {
            console.error("Error fetching file:", err);
            setError("Error occurred while loading file.");
            setContent(""); // Clear content
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) fetchContent();
    }, [version, open, flag]);

    useEffect(() => {
        if (!open) {
            setContent("");
            setError("");
        }
    }, [open]);

    // Title based on flag
    const getTitle = () => {
        if (flag === 'config') return "hyperdart Config";
        if (flag === 'resource') return "Resource JSON";
        return "Latest Release Notes";
    };

    const getMarkdownContent = () => {
        if (!content) return "";
        if (flag === 'config') return `\`\`\`javascript\n${content}\n\`\`\``;
        if (flag === 'resource') return `\`\`\`json\n${content}\n\`\`\``;
        return content;
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md"
            PaperProps={{ sx: { borderRadius: 2, p: 1 } }}
        >
            <DialogTitle sx={{ fontWeight: 600, fontSize: "20px" }}>
                {getTitle()}
                <IconButton onClick={handleClose} sx={{ position: "absolute", right: 8, top: 8 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        v{version}
                    </Typography>
                    {publishDate && (
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            Published on {publishDate}
                        </Typography>
                    )}
                </Box>

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <Loader />
                    </Box>
                ) : error ? (
                    <Typography sx={{ color: "red", fontWeight: 600, textAlign: "center", py: 2 }}>
                        {error}
                    </Typography>
                ) : (
                    <Box
                        sx={{
                            typography: { sm: "body2", xs: "subtitle1" },
                            color: "#333",
                            lineHeight: 1.6,
                            "& p": { mb: 1.2 },
                            "& ul": { pl: 3, mb: 1.5 },
                            "& h3": { fontSize: "16px", fontWeight: 700, mt: 2, mb: 1 },
                        }}
                    >
                        <ReactMarkdown>{getMarkdownContent()}</ReactMarkdown>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default FileViewerPopup;
